import { GuestMessagingApiService } from '@api_patient/guest_messaging.api.service';
import { IdentityApiService } from '@api_patient/identity_api.service';
import { MessagingConversationsApiService } from '@api_patient/messaging_conversations_api.service';
import { HeaderStateService } from '@application/header_state.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { DateService } from '@application/time/date.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { RouterService } from '@cortex-shim/router.service';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { signal } from '@preact/signals-react';
import { ControllerProtocol, Injectable, Scopes, useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';

type AnonymousChatFlowScreen = 'start' | 'initial-message' | 'chat';

interface AnonymousChatState {
	displayName: string | null;
	initialMessage: string | null;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class ViewAnonymousChatController implements ControllerProtocol {
	private readonly routeBase: string;
	private readonly receptionId: number;
	// biome-ignore lint/correctness/useHookAtTopLevel: Should refactor
	private readonly headerStateService = useResolve(HeaderStateService);
	private readonly logger = new Logger('ViewAnonymousChatController');

	public readonly state = signal<AnonymousChatState>({
		displayName: null,
		initialMessage: null,
	});

	constructor(
		private readonly routerService: RouterService,
		private readonly unitService: UnitService,
		private readonly universalConfig: UniversalConfig,
		private readonly guestMessagingApiService: GuestMessagingApiService,
		private readonly identityApiService: IdentityApiService,
		private readonly messagingConversationsApiService: MessagingConversationsApiService,
		private readonly dateService: DateService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;

		const receptionId = unit.receptionId;
		this.receptionId = receptionId;
	}

	// biome-ignore lint/suspicious/noExplicitAny: Should refactor
	async whenMount(...args: any[]) {
		const currentPath = window.location.pathname;
		// If user has somehow routed to the initial message screen without a display name, redirect them back
		if (currentPath.includes('initial-message') && this.state.value.displayName == null) {
			this.goToScreen('start');
		}

		this.headerStateService.setVisibility({
			showSearchReception: false,
			showMyVisits: false,
			showUserProfile: false,
			showSignOut: false,
		});
	}

	// biome-ignore lint/suspicious/noExplicitAny: Should refactor
	async whenDemount(...args: any[]) {
		this.headerStateService.reset();
	}

	public async signOutAndRedirect(redirectUrl: string = this.universalConfig.alias) {
		// Create and submit sign-out form
		const form = document.createElement('form');
		form.method = 'post';
		form.action = this.identityApiService.getSignOutPath(redirectUrl);
		form.style.display = 'none';

		document.body.appendChild(form);
		form.submit();
	}

	public goToReceptionHome() {
		// Client-side routerService.navigateByUrl leaves this flow's route tree stuck rendered
		// after leaving a flow route (e.g. this anonymous-chat flow); a real URL change reliably resets it.
		// The router's basename (licence holder alias) isn't applied to window.location, so it's prepended here.
		window.location.href = getAbsoluteRoute([this.universalConfig.alias, this.routeBase].filter(Boolean).join('/'));
	}

	public setDisplayName(displayName: string): void {
		this.state.value = {
			...this.state.value,
			displayName,
		};
	}

	public setInitialMessage(message: string): void {
		this.state.value = {
			...this.state.value,
			initialMessage: message,
		};
	}

	public handleSocketError(caseId: string | undefined, type: 'connectionError' | 'unauthorized'): void {
		this.logger.logError(`Socket threw ${type} for case ${caseId}:`);
		this.signOutAndRedirect();
	}

	public async closeCase(caseId: string): Promise<void> {
		const result = await this.messagingConversationsApiService.endMessagingConversation(caseId);

		if (result.err) {
			this.logger.logError(`Failed to close messaging case with id ${caseId}:`, result.err);
		}
	}

	public async fetchConversationMetadata(caseId: string): Promise<MessagingMetaData | null> {
		const result = await this.messagingConversationsApiService.getMessagingConversation(caseId);
		if (result.err) {
			this.logger.logError(`Failed to fetch messaging case with id ${caseId}:`, result.err);

			if (result.err.statusCode === 401) {
				this.signOutAndRedirect();
			}
			return null;
		}

		const caseData = result.ok.data;

		return {
			hasReadUntilTimestamp: caseData.HasReadUntilTimestamp ? this.dateService.newDate(caseData.HasReadUntilTimestamp) : undefined,
			lastMessageTimestamp: caseData.LastMessageTimestamp ? this.dateService.newDate(caseData.LastMessageTimestamp) : undefined,
			allowClientResponse: caseData.AllowClientResponse,
			allowClientToComplete: caseData.AllowClientToComplete,
			completedTimestamp: caseData.CompletedTimestamp ? this.dateService.newDate(caseData.CompletedTimestamp) : undefined,
			receiptUrl: null,
			resourceInfo: caseData.ResourceInfo ?? null,
		};
	}

	public async startChat(initialMessage: string) {
		const displayName = this.state.value.displayName;

		if (displayName == null) {
			this.goToScreen('start');
			return;
		}

		const response = await this.guestMessagingApiService.create(this.receptionId, displayName, initialMessage);

		if (response.err) {
			throw new Error(I18nAndL10n.translate('Generic.Case.Submit.Error'));
		}

		const returnUrl = `${window.location.href.split('initial-message')[0]}chat/${response.ok.data.CaseId}`;
		const redirectUrl = `${response.ok.data.LoginUrl}&returnUrl=${returnUrl}`;

		window.location.href = redirectUrl;

		// Quirk:
		// The loading state of the form is based on promise resolution.
		// Since we are redirecting away from the page, we never want to resolve the promise.
		await new Promise(() => {
			// Intentionally empty
		});
	}

	public goToScreen(screen: AnonymousChatFlowScreen, options: { conversationId?: number } = {}): void {
		let path = '';

		switch (screen) {
			case 'start':
				path = '';
				break;
			case 'initial-message':
				path = 'initial-message';
				break;
			case 'chat':
				// Handled below
				if (options.conversationId != null && options.conversationId != undefined) {
					path = `chat/${options.conversationId}`;
				} else {
					// If no conversationId is provided, default to start screen
					path = '';
				}
				break;
			default:
				throw new Error(`Unknown screen: ${screen}`);
		}

		const url = [this.routeBase, 'message/anonymous-chat', path].filter(Boolean);

		this.routerService.navigateByUrl(`/${url.join('/')}`);
	}
}
